import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import { DrawingOptions, DrawingSettingGroup, DrawingTypes, LabelAxisViewConfig, SettingValues } from '../drawing.interface'
import { Coordinate, MouseEventParams, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { determineChartX, determineChartY, scaledCoordinate } from '../drawing.helper'
import { ChartEvents } from '../../chart.events'
import { Broker_OrderType, Broker_Variety, Order, OrderType, Product, productToJSON, TransactionType, Validity } from '../../../../proto/generated/order'
import { Position } from '../../../../proto/generated/position'
import { blue, green, red } from '../../../../assets/colors'
import { alpha } from '@mui/material'
import { v4 as uuid } from 'uuid'

export type ChartOrdersActions = 'chartOrdersActions'

export enum PositionComponentTypes {
    REVERSE_BTN = 'REVERSE_BTN',
    TARGET_PRICE_BTN = 'TARGET_PRICE_BTN',
    STOPLOSS_BTN = 'STOPLOSS_BTN',
    QUANTITY_BTN = 'QUANTITY_BTN',
    PRICE_BTN = 'PRICE_BTN',
    CANCEL_BTN = 'CANCEL_BTN',
    MODIFY_ORDER_LINE = 'MODIFY_ORDER_LINE',
    DISCARD_BTN = 'DISCARD_BTN',
    CONFIRM_BTN = 'CONFIRM_BTN',
    PRODUCT = 'PRODUCT',
    ALERT = 'ALERT'
}

export type OrdersOnChartToolState = {
    positions: {
        position: Position & {
            isBasket?: boolean,
            realisedProfit?: number,
        }
        stopLossOrder: Order | null
        targetOrder: Order | null
    }[]
    openOrders: {
        order: Order
        SL: Order | null
        TP: Order | null
    }[]
    ltp: number
    topBid: number
    topAsk: number
}

export type OrdersOnChartToolOptions = {
    drawingType: ChartOrdersActions
}

export type OrdersOnChartToolDrawingSettings = {
    positions: {
        position: Position & {
            isBasket?: boolean
        }
        stopLossOrder: Order | null
        targetOrder: Order | null
    }[]
    openOrders: {
        order: Order
        SL: Order | null
        TP: Order | null
    }[]
    ltp: number
    topBid: number
    topAsk: number
}

const priceMultiplier = 100
export const yellow = '#FFA500'
const DRAG_THRESHOLD = 3
const positionRegex = /^position-\d+$/
const positionStopLossRegex = /^position-stop-loss-order-\d+$/
const positionTargetRegex = /^position-target-order-\d+$/
const openOrderRegex = /^open-order-\d+$/
const openOrderStopLossRegex = /^stop-loss-order-\d+$/
const openOrderTargetRegex = /^target-order-\d+$/

type HandleScrollSnapshot = {
    mouseWheel: boolean
    pressedMouseMove: boolean
    pressedHorizontalMouseMove: boolean
    pressedVerticalMouseMove: boolean
    horzTouchDrag: boolean
    vertTouchDrag: boolean
}

const defaultHandleScrollSnapshot = (): HandleScrollSnapshot => ({
    mouseWheel: true,
    pressedMouseMove: true,
    pressedHorizontalMouseMove: true,
    pressedVerticalMouseMove: true,
    horzTouchDrag: true,
    vertTouchDrag: true
})

const priority = (b: { isVisible: boolean; isHovered: boolean }) => {
    if (!b.isVisible) return 0;
    if (b.isHovered) return 2;
    return 1;
};

type PositionComponents = {
    [key in PositionComponentTypes]?: {
        x1: number
        x2: number
    }
}

class OrdersOnChartTool extends DrawingBase<
    OrdersOnChartToolOptions,
    OrdersOnChartToolDrawingSettings,
    OrdersOnChartToolState
> {
    initialPoint = 0
    fixedDrawing: boolean = true
    baseSize = 10

    positionsComponents: Record<string, PositionComponents> = {}
    priceRange: Record<string, { y1: number; y2: number; y: number }> = {}

    editOrderKey: string | null = null
    editOrders: {
        SL: Order | undefined
        order: Order | undefined
        TP: Order | undefined
    } | undefined
    editorderValid: boolean = true

    hoveredKey: string | null = null
    hoveredComponent: PositionComponentTypes | undefined

    drawingMode: 'DRAG' | 'NORMAL' = 'NORMAL'
    draggedKey: string | null = null
    dragStartY: number | undefined = undefined
    dragType: 'TARGET' | 'STOPLOSS' | 'ELEMENT' | undefined

    draggedPriceCoordinate: number | null = null

    /** Saved when order UI disables pane scroll; restored on document mouseup. */
    private handleScrollSnapshotBeforeOrderInteraction: HandleScrollSnapshot | null = null

    constructor(options: DrawingOptions<any, any, any>) {
        super(options)
        if (this._chartApi) this._chartApi.subscribeClick(this.handleChartClick)
        const paneElement = this._chart.getPaneElementById(this._paneId)
        if (paneElement) {
            if(this._chart.touchEnabled) {
                paneElement.addEventListener('touchmove', this.subscribeMouseUpDownMove)
            } else {
                paneElement.addEventListener('mousemove', this.subscribeMouseUpDownMove)
            }
            this.subscribeMouseUpDownMove(undefined)
        }
    }

    private textWidthCache = new Map<string, number>()

    measure(ctx: CanvasRenderingContext2D, text: string) {
        if (!this.textWidthCache.has(text)) {
            this.textWidthCache.set(text, ctx.measureText(text).width)
        }
        return this.textWidthCache.get(text)!
    }

    // Arrow fn so reference is stable
    private handleChartClick = (param: MouseEventParams): void => {
        const { point } = param
        if (!point) return
        const clickData = this.findHoveredData(point.x, point.y)
        if (clickData) {
            if (this.editOrderKey) {
                const [index] = clickData.key.split('-').splice(-1)
                if (clickData.actionType === PositionComponentTypes.CONFIRM_BTN) {
                    let order: Order | undefined = this.editOrders.order ? this.editOrders.order : clickData.order ? { ...clickData.order } : undefined
                    let slOrder = this.editOrders.SL ? this.editOrders.SL : clickData.slOrder ? { ...clickData.slOrder} : undefined
                    let tOrder = this.editOrders.TP ? this.editOrders.TP : clickData.tOrder ? { ...clickData.tOrder} : undefined
                    const batchId = order?.batchId ?
                            order.batchId :
                            slOrder?.batchId ?
                            slOrder.batchId :
                            tOrder?.batchId ?
                            tOrder.batchId :
                            uuid()
                            
                    const position = clickData.position
                    const basketId = position?.basketId ?
                        position.basketId :
                            order?.basketId ?
                                order.basketId :
                                    undefined
                    const entryPrice = position
                        ? (position.closePosition !== 0
                            ? position.closePositionValue / Math.abs(position.closePosition)
                            : 0)
                        : 0

                    if (slOrder) {
                        const slTriggerPrice = (slOrder?.isBasket && position)
                            ? Math.round(entryPrice - slOrder?.triggerPrice) * Math.abs(position?.closePosition)
                            : Math.round(slOrder?.triggerPrice)
                        slOrder = {
                            security: slOrder.security,
                            transactionType: slOrder.transactionType,
                            orderType: slOrder.orderType,
                            brokerOrderType: slOrder.brokerOrderType,
                            quantity: slOrder.quantity,
                            isBasket: slOrder.isBasket,
                            batchId: batchId,
                            orderDate: Date.now(),
                            product: slOrder.product,
                            price: Math.round(slOrder.price),
                            triggerPrice: slTriggerPrice,
                            brokerVariety: slOrder.brokerVariety,
                            validity: slOrder.validity,
                            clientOrderId: slOrder.clientOrderId,
                            disclosedQuantity: slOrder.disclosedQuantity,
                            orderId: slOrder.orderId,
                            basketId: basketId,
                            marketProtection: slOrder?.marketProtection??0,
                            immediateExecutePaperOrder: false
                        }
                    }
                    if (tOrder) {
                        const tPrice = (tOrder?.isBasket && position)
                            ? Math.round(tOrder.price - entryPrice) * Math.abs(position.closePosition)
                            : Math.round(tOrder.price)
                        tOrder = {
                            security: tOrder.security,
                            transactionType: tOrder.transactionType,
                            orderType: tOrder.orderType,
                            brokerOrderType: tOrder.brokerOrderType,
                            quantity: tOrder.quantity,
                            isBasket: tOrder.isBasket,
                            batchId: batchId,
                            orderDate: Date.now(),
                            product: tOrder.product,
                            price: tPrice,
                            triggerPrice: Math.round(tOrder.triggerPrice),
                            brokerVariety: tOrder.brokerVariety,
                            validity: tOrder.validity,
                            clientOrderId: tOrder.clientOrderId,
                            disclosedQuantity: tOrder.disclosedQuantity,
                            orderId: tOrder.orderId,
                            basketId: basketId,
                            immediateExecutePaperOrder: false
                        }
                    }
                    if (order) {
                        order = {
                            security: order.security,
                            transactionType: order.transactionType,
                            orderType: order.orderType,
                            brokerOrderType: order.brokerOrderType,
                            quantity: order.quantity,
                            isBasket: false,
                            batchId: batchId,
                            orderDate: Date.now(),
                            product: order.product,
                            price: Math.round(order.price),
                            triggerPrice: Math.round(order.triggerPrice),
                            brokerVariety: order.brokerVariety,
                            validity: order.validity,
                            clientOrderId: order.clientOrderId,
                            disclosedQuantity: order.disclosedQuantity,
                            orderId: order.orderId,
                            basketId: order.basketId,
                            immediateExecutePaperOrder: false
                        }
                    }

                    this._chart.publishEvent(ChartEvents.CHART_ORDER_ACTION_CLICKED, {
                        ...clickData,
                        slOrder,
                        tOrder,
                        order,
                    })
                    this.setEditOrderKey(undefined)
                } else if (clickData.actionType === PositionComponentTypes.DISCARD_BTN) {
                    this.setEditOrderKey(undefined)
                } else if (clickData.actionType === PositionComponentTypes.CANCEL_BTN) {
                    if (positionRegex.test(clickData.key)) {
                        this.setEditOrderKey(undefined)
                        this._chart.publishEvent(ChartEvents.CHART_ORDER_ACTION_CLICKED, clickData)
                        return
                    } else if (openOrderRegex.test(clickData.key) && this.editOrderKey === clickData.key) {
                        this.setEditOrderKey(undefined)
                    } else if (positionStopLossRegex.test(clickData.key) && `position-${index}` === this.editOrderKey ) {
                        this.editOrders.SL = undefined
                    } else if(positionTargetRegex.test(clickData.key) && `position-${index}` === this.editOrderKey) {
                        this.editOrders.TP = undefined
                    } else if(openOrderStopLossRegex.test(clickData.key) && `open-order-${index}` === this.editOrderKey) {
                        this.editOrders.SL = undefined
                    } else if(openOrderTargetRegex.test(clickData.key) && `open-order-${index}` === this.editOrderKey) {
                        this.editOrders.TP = undefined
                    }
                    if(!this.editOrders || Object.values(this.editOrders).every(value => !value)) this.setEditOrderKey(undefined)
                }
                return
            }
            if ([PositionComponentTypes.CANCEL_BTN, PositionComponentTypes.REVERSE_BTN].includes(clickData.actionType)) {
                this.setEditOrderKey(undefined)
            }
            this._chart.publishEvent(ChartEvents.CHART_ORDER_ACTION_CLICKED, clickData)
        }
    }

    roundPriceToTickSize = (price: number) => {
        const tickSize = this._chart?.securities?.[0]?.tickSize || 5
        const roundedPrice = (
            (Math.round(Math.round(price * priceMultiplier) / tickSize) * tickSize) /
            priceMultiplier
        ).toFixed(2)

        return roundedPrice
    }

    subscribeMouseUpDownMove = (event: TouchEvent | MouseEvent | undefined) => {
        if (this.subscribedPaneIndex !== this.paneIndex) {
            this.subscribedPaneIndex = this.paneIndex
            const paneElement = this._chart.getPaneElementById(this._paneId)
            if (!paneElement) return

            this.setPaneElement(paneElement.id, paneElement)

            // Ensure we don't stack listeners
            if (this._chart.touchEnabled) {
                paneElement.removeEventListener('touchstart', this.onMouseDownClick)
                document.removeEventListener('touchend', this.onMouseUpClick)
                paneElement.addEventListener('touchstart', this.onMouseDownClick, { passive: true })
                document.addEventListener('touchend', this.onMouseUpClick, { passive: true })
            } else {
                paneElement.removeEventListener('mousedown', this.onMouseDownClick)
                document.removeEventListener('mouseup', this.onMouseUpClick)
                paneElement.addEventListener('mousedown', this.onMouseDownClick)
                document.addEventListener('mouseup', this.onMouseUpClick)
            }
        }

        if (!this.dragType || !event) return

        const touch = (event as TouchEvent)?.touches?.[0]

        const chartX = determineChartX(this._chartApi, this._chartBounds, this._chart.touchEnabled ? (touch?.clientX ?? 0) : ((event as MouseEvent)?.clientX ?? 0))
        const chartY = determineChartY(this._chartApi, this._chartBounds, this._chart.touchEnabled ? (touch?.clientY ?? 0) : ((event as MouseEvent)?.clientY ?? 0))

        if (chartY == null || chartX == null) return

        const dy = Math.abs(chartY - this.dragStartY)

        if (this.drawingMode !== 'DRAG' && dy > DRAG_THRESHOLD) {
            this.drawingMode = 'DRAG'
        }

        this._chart.callbacks.onTooltipDataChange?.(false, '', 0, 0)

        // Calculate minimum allowed coordinate for non-strategy positions
        let constrainedChartY = chartY
        if (this.draggedKey) {
            const [index] = this.draggedKey.split('-').splice(-1)
            let isStrategyPosition = false
            
            // Check if this is a strategy position (basket position)
            if (positionRegex.test(this.draggedKey) || positionStopLossRegex.test(this.draggedKey) || positionTargetRegex.test(this.draggedKey)) {
                const position = this._state.positions[Number(index)]?.position
                isStrategyPosition = position?.isBasket ?? false
            }
            
            // Apply minimum price constraint for non-strategy positions
            if (!isStrategyPosition) {
                const tickSize = this._chart?.securities?.[0]?.tickSize || 5
                const minPrice = tickSize / priceMultiplier
                const minCoordinate = this.priceToCoordinate(minPrice) as number
                
                // Prevent dragging below minimum price
                if (chartY > minCoordinate) {
                    constrainedChartY = minCoordinate
                }
            }
        }

        this.draggedPriceCoordinate = scaledCoordinate(constrainedChartY, this.verticalPixelRatio)

        this.updateDrawing()

        // this._chart.setCrossHairPosition(
        //     Number(roundedPrice),
        //     this.coordinateToTime(chartX) as number
        // )
    }

    unSubscribeMouseUpDownMove = () => {
        const paneElement = this._paneElement
        if (!paneElement) return
        if (this._chart.touchEnabled) {
            paneElement.removeEventListener('touchstart', this.onMouseDownClick)
            document.removeEventListener('touchend', this.onMouseUpClick)
            paneElement.removeEventListener('touchmove', this.subscribeMouseUpDownMove)
        } else {
            paneElement.removeEventListener('mousedown', this.onMouseDownClick)
            document.removeEventListener('mouseup', this.onMouseUpClick)
            paneElement.removeEventListener('mousemove', this.subscribeMouseUpDownMove)
        }
    }

    private findHoveredData(
        x: number,
        y: number
    ): {
        position?: Position
        slOrder?: Order
        tOrder?: Order
        order?: Order
        actionType: PositionComponentTypes
        key: string
    } | null {
        const sx = scaledCoordinate(x, this.horizontalPixelRatio)
        const sy = scaledCoordinate(y, this.verticalPixelRatio)
        const { positions, openOrders: originalOpenOrders } = this._state
        const openOrders = [...originalOpenOrders]

        if (this.editOrderKey && this.editOrderKey.includes('open-order-')) {
            const [index] = this.editOrderKey.split('-').splice(-1)
            if (Number(index) >= openOrders.length) openOrders.push(this.editOrders)
        }

        if ((!positions || positions.length === 0) && (!openOrders || openOrders.length === 0)) {
            return null
        }

        if (this.drawingMode === 'NORMAL') {
            // Current position
            for (let i=0; i<positions.length; i++) {
                const { position, stopLossOrder: slOrderOriginal, targetOrder: targetOrderOriginal} = positions[i]
                if (position) {
                    const key = `position-${i}`
                    const data = this.findData(key, 'position', position, sx, sy, position.isBasket)

                    const stopLossOrder = (this.editOrderKey === key && this.editOrders?.SL) ? this.editOrders.SL : slOrderOriginal
                    const targetOrder = (this.editOrderKey === key && this.editOrders?.TP) ? this.editOrders.TP : targetOrderOriginal
                    if (data) return {
                        ...data,
                        slOrder: stopLossOrder,
                        tOrder: targetOrder
                    }

                    if (stopLossOrder) {
                        const key = `position-stop-loss-order-${i}`
                        const data = this.findData(key, 'order', stopLossOrder, sx, sy, position.isBasket)
                        if (data) return data
                    }
        
                    if (targetOrder) {
                        const key = `position-target-order-${i}`
                        const data = this.findData(key, 'order', targetOrder, sx, sy, position.isBasket)
                        if (data) return data
                    }
                }
            }

            // Open orders
            for (let i = 0; i < openOrders.length; i++) {
                let order = openOrders[i].order
                let stopLossOrder = openOrders[i].SL
                let targetOrder = openOrders[i].TP
                const key = `open-order-${i}`
                if (this.editOrderKey === key) {
                    if (this.editOrders.order) order = this.editOrders.order
                    if (this.editOrders.SL) stopLossOrder = this.editOrders.SL
                    if (this.editOrders.TP) targetOrder = this.editOrders.TP
                }
                const data = this.findData(key, 'order', order, sx, sy)
                if (data) return data
                if (stopLossOrder) {
                    const slKey = `stop-loss-order-${i}`
                    const slData = this.findData(slKey, 'order', stopLossOrder, sx, sy)
                    if (slData) return slData
                }
                if (targetOrder) {
                    const tKey = `target-order-${i}`
                    const tData = this.findData(tKey, 'order', targetOrder, sx, sy)
                    if (tData) return tData
                }
            }
        } else if (this.draggedKey) {
            const dataKey: 'position' | 'order' = positionRegex.test(this.draggedKey)
                ? 'position'
                : 'order'
            const [indexStr] = this.draggedKey.split('-').splice(-1)
            const index = Number(indexStr)

            let d: Position | Order = null
            switch (this.draggedKey) {
                case `position-${index}`:
                    d = positions[index].position
                    break
                case `position-stop-loss-order-${index}`:
                    d =  positions[index].stopLossOrder
                    break
                case `position-target-order-${index}`:
                    d = positions[index].targetOrder
                    break
                case `open-order-${index}`:
                    d = openOrders[index].order
                    break
                case `stop-loss-order-${index}`:
                    d = openOrders[index].SL
                    break
                case `target-order-${index}`:
                    d = openOrders[index].TP
                    break
            }
            if (d) {
                const data = this.findData(this.draggedKey, dataKey, d, sx, sy)
                if (data) return data
            }
        }

        return null
    }

    private findData(
        key: string,
        dataKey: 'order' | 'position',
        data: Position | Order,
        sx: number,
        sy: number,
        isBasket: boolean = false,
    ): | {
        position?: Position & {isBasket?: boolean}
        order?: Order
        actionType: PositionComponentTypes
        key: string,
        isBasket?: boolean
    } | null {
        const priceRange = this.priceRange[key]
        if (!priceRange) return null

        const { y1, y2, y } = priceRange

        if (this.drawingMode === 'DRAG' && this.draggedKey === key && this.dragType && (this.dragType === 'TARGET' || this.dragType === 'STOPLOSS')) {
            return {
                [dataKey]: data,
                actionType:
                    this.dragType === 'TARGET'
                        ? PositionComponentTypes.TARGET_PRICE_BTN
                        : PositionComponentTypes.STOPLOSS_BTN,
                key
            }
        }

        const components = this.positionsComponents[key]
        const vRatio = this.verticalPixelRatio
        const hRatio = this.horizontalPixelRatio

        if (sy >= y1 && sy <= y2 && components) {
            const chartY = y / vRatio

            // Helper to avoid code duplication
            const checkHit = (
                comp: PositionComponentTypes,
                label: string,
                tooltip: string,
                tooltipVisible: boolean
            ) => {
                const c = components[comp]
                if (!c) return false
                if (sx < c.x1 || sx > c.x2) return false

                const chartX = ((c.x1 + c.x2) / 2) / hRatio
                this.hoveredKey = key
                this.hoveredComponent = comp

                if (tooltip || tooltipVisible === false) {
                    this._chart.callbacks.onTooltipDataChange?.(
                        tooltipVisible,
                        tooltip,
                        chartX,
                        chartY
                    )
                }

                return true
            }

            const evaluateAboveAndBelow = (orderType: OrderType, transactionType: TransactionType) => {
                let result = ''
                if(orderType && transactionType) {
                    if(isBasket) {
                        if(orderType === OrderType.STOP_LOSS) {
                            result = 'below'
                        } else if(orderType === OrderType.TARGET) {
                            result = 'above'
                        }
                    } else {
                        if(orderType === OrderType.STOP_LOSS) {
                            if(transactionType === TransactionType.BUY) {
                                result = 'above'
                            } else if(transactionType === TransactionType.SELL) {
                                result = 'below'
                            }
                        } else if(orderType === OrderType.TARGET) {
                            if(transactionType === TransactionType.BUY) {
                                result = 'below'
                            } else if(transactionType === TransactionType.SELL) {
                                result = 'above'
                            }
                        }
                    }
                }
                return result
            }

            if (checkHit(PositionComponentTypes.CANCEL_BTN, dataKey === 'order' ? 'Cancel Order' : 'Close Position', dataKey === 'order' ? 'Cancel Order' : 'Close Position', true)
            ) {
                return {
                    [dataKey]: data,
                    actionType: PositionComponentTypes.CANCEL_BTN,
                    key,
                    isBasket: dataKey === 'position' ? (data as Position & {isBasket?: boolean})?.isBasket ?? false : false
                }
            }

            const priceBtnTooltip = (() => {
                if (!isBasket || dataKey !== 'position') return ''
                const pos = data as Position & { realisedProfit?: number }
                const posPrice = (pos.closePosition !== 0
                    ? pos.closePositionValue / Math.abs(pos.closePosition)
                    : 0) / priceMultiplier
                const ltp = this._state.ltp
                const qty = Math.abs(pos.closePosition)
                const unrealized = posPrice <= ltp
                    ? Math.abs(ltp - posPrice) * qty
                    : -1 * Math.abs(posPrice - ltp) * qty
                const realized = pos.realisedProfit || 0
                return `Unrealized: ₹${unrealized > 0 ? `₹${unrealized.toFixed(2)}` : `-₹${unrealized.toFixed(2)}`}\nRealized: ${ realized > 0 ? `₹${realized.toFixed(2)}` : `-₹${realized.toFixed(2)}`}`
            })()

            if (
                checkHit(
                    PositionComponentTypes.PRICE_BTN,
                    'price btn',
                    priceBtnTooltip,
                    isBasket && dataKey === 'position'
                )
            ) {
                return {
                    [dataKey]: data,
                    actionType: PositionComponentTypes.PRICE_BTN,
                    key
                }
            }

            if (
                checkHit(
                    PositionComponentTypes.QUANTITY_BTN,
                    (dataKey === 'order' && key.includes('open-order-')) ? 'Modify Quantity' : '',
                    (dataKey === 'order' && key.includes('open-order-')) ? 'Modify Quantity' : '',
                    true
                )
            ) {
                return {
                    [dataKey]: data,
                    actionType: PositionComponentTypes.QUANTITY_BTN,
                    key
                }
            }

            if (
                checkHit(
                    PositionComponentTypes.REVERSE_BTN,
                    'Reverse Position',
                    'Reverse Position',
                    true
                )
            ) {
                return {
                    [dataKey]: data,
                    actionType: PositionComponentTypes.REVERSE_BTN,
                    key
                }
            }

            if (
                checkHit(
                    PositionComponentTypes.ALERT,
                    'Order Not Possible',
                    (data as Order)?.orderType === OrderType.TRIGGER ? `Selected price isn't valid for this order.` : `${(data as Order)?.orderType === OrderType.TARGET ? 'Take profit' : (data as Order)?.orderType === OrderType.STOP_LOSS ? 'Stop loss' : 'Order'} must be ${evaluateAboveAndBelow((data as Order)?.orderType, (data as Order)?.transactionType)} ${!key.includes('position-') ? 'entry price' : isBasket ? 'current price' : (data as Order)?.transactionType === TransactionType.SELL ? ( this._state.topBid ? 'current bid' : 'last traded price' ) : ( this._state.topAsk ? 'current ask' : 'last traded price')}.`,
                    true
                )
            ) {
                return {
                    [dataKey]: data,
                    actionType: PositionComponentTypes.ALERT,
                    key
                }
            }

            if (
                checkHit(
                    PositionComponentTypes.STOPLOSS_BTN,
                    'Drag to add Stop loss',
                    'Drag to add Stop loss',
                    true
                )
            ) {
                return {
                    [dataKey]: data,
                    actionType: PositionComponentTypes.STOPLOSS_BTN,
                    key
                }
            }

            if (
                checkHit(
                    PositionComponentTypes.TARGET_PRICE_BTN,
                    'Drag to add Take profit',
                    'Drag to add Take profit',
                    true
                )
            ) {
                return {
                    [dataKey]: data,
                    actionType: PositionComponentTypes.TARGET_PRICE_BTN,
                    key
                }
            }

            if (components.CONFIRM_BTN && sx >= components.CONFIRM_BTN.x1 && sx <= components.CONFIRM_BTN.x2) {
                if(!this.editorderValid) {
                    return null
                }
                this.hoveredKey = key
                this.hoveredComponent = PositionComponentTypes.CONFIRM_BTN
                return {
                    [dataKey]: data,
                    actionType: PositionComponentTypes.CONFIRM_BTN,
                    key
                }
            }

            if (components.DISCARD_BTN && sx >= components.DISCARD_BTN.x1 && sx <= components.DISCARD_BTN.x2) {
                this.hoveredKey = key
                this.hoveredComponent = PositionComponentTypes.DISCARD_BTN
                return {
                    [dataKey]: data,
                    actionType: PositionComponentTypes.DISCARD_BTN,
                    key
                }
            }

            if (components.PRODUCT && sx >= components.PRODUCT.x1 && sx <= components.PRODUCT.x2) {
                this.hoveredKey = key
                this.hoveredComponent = PositionComponentTypes.PRODUCT
                return {
                    [dataKey]: data,
                    actionType: PositionComponentTypes.PRODUCT,
                    key
                }
            }
        }

        // Line hit for order
        const pricePoint = y
        const threshold = scaledCoordinate(2, vRatio)
        if (
            dataKey === 'order' &&
            sy >= pricePoint - threshold &&
            sy <= pricePoint + threshold
        ) {
            this.hoveredKey = key
            this.hoveredComponent = PositionComponentTypes.MODIFY_ORDER_LINE
            this._chart.callbacks.onTooltipDataChange?.(false, '', 0, 0)
            return {
                [dataKey]: data as Order,
                actionType: PositionComponentTypes.MODIFY_ORDER_LINE,
                key
            }
        }

        return null
    }

    draw(scope: BitmapCoordinatesRenderingScope) {
        const ctx = scope.context
        const { positions, ltp, openOrders: originalOpenOrders } = this._state
        const openOrders = [...originalOpenOrders]

        if (this.editOrderKey && this.editOrderKey.includes('open-order-')) {
            const [index] = this.editOrderKey.split('-').splice(-1)
            if (Number(index) >= openOrders.length) openOrders.push(this.editOrders)
        }

        if ((!positions || positions.length === 0) && (!openOrders || openOrders.length === 0)) {
            this.positionsComponents = {}
            this.priceRange = {}
            return
        }

        const validKeys = new Set<string>()
        positions.forEach((_, i) => {
            validKeys.add(`position-${i}`)
            validKeys.add(`position-stop-loss-order-${i}`)
            validKeys.add(`position-target-order-${i}`)
        })
        openOrders.forEach((_, i) => {
            validKeys.add(`open-order-${i}`)
            validKeys.add(`stop-loss-order-${i}`)
            validKeys.add(`target-order-${i}`)
        })

        Object.keys(this.positionsComponents).forEach(key => {
            if (!validKeys.has(key)) {
                delete this.positionsComponents[key]
            }
        })
        Object.keys(this.priceRange).forEach(key => {
            if (!validKeys.has(key)) {
                delete this.priceRange[key]
            }
        })

        const visibleLogicalRange = this._chartApi.timeScale().getVisibleLogicalRange()
        if (!visibleLogicalRange) return

        const barsInfo = this.getSeries().barsInLogicalRange(visibleLogicalRange)
        if (!barsInfo) return

        const blocks: {
            key: string
            draw: () => void
            isHovered: boolean
            isVisible: boolean
        }[] = [];

        const { palette } = this._chart.theme
        const backgroundPaper = palette.background.paper
        const textPrimary = palette.text.primary
        const vRatio = this.verticalPixelRatio
        const canvasWidth = this.canvasWidth

        positions.forEach((p,i) => {
            const key = `position-${i}`
            const slKey = `position-stop-loss-order-${i}`
            const tKey = `position-target-order-${i}`
            const { position, stopLossOrder: slOrderOriginal, targetOrder: targetOrderOriginal } = p
            const stopLossOrder = (this.editOrderKey === key && this.editOrders?.SL) ? this.editOrders.SL : slOrderOriginal
            const targetOrder = (this.editOrderKey === key && this.editOrders?.TP) ? this.editOrders.TP : targetOrderOriginal
            const setEditOrderValidKey = this.editOrderKey === key
            let editOrderValidKey = true
            if (position) {
                const visible = [key, slKey, tKey].includes(this.hoveredKey)
                const hovered = this.hoveredKey === key
                const transactionType: TransactionType =
                    position.closePosition < 0
                        ? TransactionType.SELL
                        : position.closePosition > 0
                            ? TransactionType.BUY
                            : undefined
    
                if (transactionType) {
                    const primaryColor =
                        transactionType === TransactionType.BUY ? blue : red
    
                    let price =
                        (position.closePosition !== 0
                            ? position.closePositionValue / Math.abs(position.closePosition)
                            : 0) / priceMultiplier
                    if(!position?.isBasket) price = Math.abs(price)
                    const quantity = Math.abs(position.closePosition)
    
                    let y = this.priceToCoordinate(price) as number
                    let SLy: number = null
                    let TPy: number = null
                    if (y) {
                        y = scaledCoordinate(y, vRatio)
                    }

                    const unrealizedProfit = position?.isBasket
                        ? (ltp - price) * quantity
                        : transactionType === TransactionType.BUY
                            ? (ltp - price) * quantity
                            : (price - ltp) * quantity

                    const realizedProfit = position?.isBasket ? (position?.realisedProfit || 0) : 0
                    const profit = unrealizedProfit + realizedProfit
                    blocks.push({
                        key,
                        isHovered: this.hoveredKey ? hovered : true,
                        isVisible: this.hoveredKey ? visible : true,
                        draw: () => {
                            this.drawTradeBox(
                                ctx,
                                canvasWidth - scaledCoordinate(50, this.horizontalPixelRatio),
                                y,
                                quantity,
                                profit,
                                'INR',
                                primaryColor,
                                backgroundPaper,
                                textPrimary,
                                key,
                                transactionType,
                                (this.hoveredKey && visible) ? position.product : undefined,
                                undefined,
                                true,
                                position.isBasket ?? false,
                                'position',
                                !Boolean(stopLossOrder),
                                !Boolean(targetOrder)
                            )
                        }
                    })
    
                    if (stopLossOrder) {
                        const primary =
                            stopLossOrder.brokerOrderType === Broker_OrderType.LIMIT_ORDER
                                ? green
                                : yellow
                        const slPrice =
                            (stopLossOrder.brokerOrderType === Broker_OrderType.LIMIT_ORDER
                                ? stopLossOrder.price
                                : stopLossOrder.brokerOrderType === Broker_OrderType.STOPLOSS_ORDER
                                    ? stopLossOrder.triggerPrice
                                    : stopLossOrder.brokerOrderType === Broker_OrderType.STOPLOSS_MARKET_ORDER
                                        ? stopLossOrder.triggerPrice
                                        : 0) / priceMultiplier
                        const quantity = stopLossOrder.quantity
                        let slPriceFinal = slPrice
    
                        let y = this.priceToCoordinate(slPrice) as number
                        if (y) {
                            y = scaledCoordinate(y, vRatio)
                            if (this.drawingMode === 'DRAG' && this.draggedKey === slKey) {
                                y = this.draggedPriceCoordinate!
                                slPriceFinal = this.coordinateToPrice(this.draggedPriceCoordinate / this.verticalPixelRatio as Coordinate)
                            }
                            SLy = y
                        }

                        const slUnrealizedProfit = position?.isBasket
                            ? (slPriceFinal - price) * Math.abs(position.closePosition)
                            : transactionType === TransactionType.BUY
                                ? (slPriceFinal - price) * quantity
                                : (price - slPriceFinal) * quantity
                        const slRealizedProfit = position?.isBasket ? (position.realisedProfit || 0) : 0
                        const loss = slUnrealizedProfit + slRealizedProfit
                        let validOrder = true
                        if (position?.isBasket) {
                            validOrder = slPriceFinal < ltp;
                        } else {
                            validOrder = transactionType === TransactionType.BUY ? slPriceFinal < (this._state.topBid || this._state.ltp) : slPriceFinal > (this._state.topAsk || this._state.ltp)
                        }

                        editOrderValidKey = validOrder

                        blocks.push({
                            key: slKey,
                            isHovered: this.hoveredKey ? this.hoveredKey === slKey : true,
                            isVisible: this.hoveredKey ? visible : true,
                            draw: () => {
                                this.drawTradeBox(
                                    ctx,
                                    canvasWidth -
                                    scaledCoordinate(50, this.horizontalPixelRatio),
                                    y,
                                    quantity,
                                    loss,
                                    'INR',
                                    primary,
                                    backgroundPaper,
                                    textPrimary,
                                    slKey,
                                    stopLossOrder.transactionType,
                                    undefined,
                                    undefined,
                                    validOrder,
                                    position.isBasket ?? false,
                                    'stopLossOrder'
                                )
                            }
                        })
                    }
    
                    if (targetOrder) {
                        const primary =
                            targetOrder.brokerOrderType === Broker_OrderType.LIMIT_ORDER
                                ? green
                                : yellow
                        const tlPrice =
                            (targetOrder.brokerOrderType === Broker_OrderType.LIMIT_ORDER
                                ? targetOrder.price
                                : (targetOrder.brokerOrderType === Broker_OrderType.STOPLOSS_MARKET_ORDER || targetOrder.brokerOrderType === Broker_OrderType.STOPLOSS_ORDER)
                                    ? targetOrder.triggerPrice
                                    : 0) / priceMultiplier
                        const quantity = targetOrder.quantity
                        let tlPriceFinal = tlPrice
    
                        let y = this.priceToCoordinate(tlPrice) as number
                        if (y) {
                            y = scaledCoordinate(y, vRatio)
                            if (this.drawingMode === 'DRAG' && this.draggedKey === tKey) {
                                y = this.draggedPriceCoordinate!
                                tlPriceFinal = this.coordinateToPrice(this.draggedPriceCoordinate / this.verticalPixelRatio as Coordinate)
                            }
                            TPy = y
                        }

                        const tpUnrealizedProfit = position?.isBasket
                            ? (tlPriceFinal - price) * Math.abs(position.closePosition)
                            : transactionType === TransactionType.BUY
                                ? (tlPriceFinal - price) * quantity
                                : (price - tlPriceFinal) * quantity
                        const tpRealizedProfit = position?.isBasket ? (position?.realisedProfit || 0) : 0
                        const profit = tpUnrealizedProfit + tpRealizedProfit
                        let validOrder = true
                        if(position?.isBasket){
                            validOrder = tlPriceFinal > ltp;
                        }else{
                            validOrder = transactionType === TransactionType.BUY ? tlPriceFinal > (this._state.topBid || this._state.ltp) : tlPriceFinal < (this._state.topAsk || this._state.ltp)
                        }
                        editOrderValidKey = !editOrderValidKey ? false : validOrder
                        blocks.push({
                            key: tKey,
                            isHovered: this.hoveredKey ? this.hoveredKey === tKey : true,
                            isVisible: this.hoveredKey ? visible : true,
                            draw: () => {
                                this.drawTradeBox(
                                    ctx,
                                    canvasWidth -
                                    scaledCoordinate(50, this.horizontalPixelRatio),
                                    y,
                                    quantity,
                                    profit,
                                    'INR',
                                    primary,
                                    backgroundPaper,
                                    textPrimary,
                                    tKey,
                                    targetOrder.transactionType,
                                    undefined,
                                    undefined,
                                    validOrder,
                                    position.isBasket ?? false,
                                    'targetOrder'
                                )
                            }
                        })
                    }
                    if ([key, slKey, tKey].includes(this.hoveredKey)) {
                        blocks.push({
                            key: 'connector-lines',
                            isHovered: true,
                            isVisible: true,
                            draw: () => {
                                this.drawBracketOrderConnectingLines(ctx, y, SLy, TPy, primaryColor)
                            }
                        })
                    }
                }
            }
            if(setEditOrderValidKey) {
                const hasIncomingOrders = (position as Position & {hasIncomingOrders?: boolean})?.hasIncomingOrders ?? false
                this.editorderValid = hasIncomingOrders ? false : editOrderValidKey
            }
        })

        if (openOrders && openOrders.length) {
            for (let i = 0; i < openOrders.length; i++) {
                const key = `open-order-${i}`
                const slKey = `stop-loss-order-${i}`
                const tKey = `target-order-${i}`
                let order = openOrders[i].order
                let stopLossOrder = openOrders[i].SL
                let targetOrder = openOrders[i].TP
                let setEditOrderValidKey = false
                let validOrderKey = true
                if (this.editOrderKey === key) {
                    setEditOrderValidKey = true
                    if (this.editOrders?.order) order = this.editOrders.order
                    if (this.editOrders?.SL) stopLossOrder = this.editOrders.SL
                    if (this.editOrders?.TP) targetOrder = this.editOrders.TP
                }
                const visible = [key, slKey, `target-order-${i}`].includes(this.hoveredKey)
                const hovered = this.hoveredKey === key
                const primaryColor =
                    order.transactionType === TransactionType.BUY ? blue : red
                const openOrderprice =
                    Math.abs((order.brokerOrderType === Broker_OrderType.LIMIT_ORDER
                        ? order.price
                        : order.brokerOrderType === Broker_OrderType.STOPLOSS_MARKET_ORDER
                            ? order.triggerPrice
                            : order.brokerOrderType === Broker_OrderType.STOPLOSS_ORDER
                                ? order.triggerPrice
                                : 0) / priceMultiplier)
                const quantity = Math.abs(order.quantity)
                let price = openOrderprice

                let y = this.priceToCoordinate(openOrderprice) as number
                let SLy: number = null
                let TPy: number = null
                if (y) {
                    y = scaledCoordinate(y, vRatio)
                    if (this.drawingMode === 'DRAG' && this.draggedKey === key && this.dragType === 'ELEMENT') {
                        y = this.draggedPriceCoordinate!
                        price = this.coordinateToPrice(this.draggedPriceCoordinate / this.verticalPixelRatio as Coordinate)
                    }
                }

                validOrderKey = order.orderType === OrderType.TRIGGER ? order.transactionType === TransactionType.BUY ? price > (this._state.topAsk || this._state.ltp) : price < (this._state.topBid || this._state.ltp) : true

                const validOpenOrder = validOrderKey

                blocks.push({
                    key,
                    isHovered: this.hoveredKey ? hovered : true,
                    isVisible: this.hoveredKey ? visible : true,
                    draw: () => {
                        this.drawTradeBox(
                            ctx,
                            canvasWidth - scaledCoordinate(50, this.horizontalPixelRatio),
                            y,
                            quantity,
                            openOrderprice,
                            'INR',
                            primaryColor,
                            backgroundPaper,
                            textPrimary,
                            key,
                            order.transactionType,
                            (this.hoveredKey && visible) ? order.product : undefined,
                            order.brokerOrderType,
                            validOpenOrder,
                            false,
                            'openOrder',
                            !Boolean(stopLossOrder) && order.orderType === OrderType.NORMAL,
                            !Boolean(targetOrder) && order.orderType === OrderType.NORMAL
                        )
                    }
                })

                if (stopLossOrder) {
                    const hovered = this.hoveredKey === slKey
                    const primary =
                        stopLossOrder.brokerOrderType === Broker_OrderType.LIMIT_ORDER
                            ? green
                            : yellow
                    const price =
                        (stopLossOrder.brokerOrderType === Broker_OrderType.LIMIT_ORDER
                            ? stopLossOrder.price
                            : stopLossOrder.brokerOrderType === Broker_OrderType.STOPLOSS_ORDER
                                ? stopLossOrder.triggerPrice
                                : stopLossOrder.brokerOrderType === Broker_OrderType.STOPLOSS_MARKET_ORDER
                                    ? stopLossOrder.triggerPrice
                                    : 0) / priceMultiplier
                    const quantity = stopLossOrder.quantity
                    let slPriceFinal = price

                    let y = this.priceToCoordinate(price) as number
                    if (y) {
                        y = scaledCoordinate(y, vRatio)
                        if (this.drawingMode === 'DRAG' && this.draggedKey === slKey && this.dragType === 'ELEMENT') {
                            y = this.draggedPriceCoordinate!
                            slPriceFinal = this.coordinateToPrice(this.draggedPriceCoordinate / this.verticalPixelRatio as Coordinate)
                        }
                        SLy = y
                    }
                    const loss = order.transactionType === TransactionType.BUY
                    ? (slPriceFinal - openOrderprice) * quantity
                    : (openOrderprice - slPriceFinal) * quantity

                    blocks.push({
                        key: slKey,
                        isHovered: this.hoveredKey ? hovered : true,
                        isVisible: this.hoveredKey ? visible : true,
                        draw: () => {
                            this.drawTradeBox(
                                ctx,
                                canvasWidth -
                                scaledCoordinate(50, this.horizontalPixelRatio),
                                y,
                                quantity,
                                loss,
                                'INR',
                                primary,
                                backgroundPaper,
                                textPrimary,
                                slKey,
                                stopLossOrder.transactionType,
                                undefined,
                                undefined,
                                loss<0,
                                false,
                                'stopLossOrder'
                            )
                        }
                    })
                    validOrderKey = !validOrderKey ? false : loss<0
                }

                if (targetOrder) {
                    const hovered = this.hoveredKey === tKey
                    const primary =
                        targetOrder.brokerOrderType === Broker_OrderType.LIMIT_ORDER
                            ? green
                            : yellow
                    const price =
                        (targetOrder.brokerOrderType === Broker_OrderType.LIMIT_ORDER
                            ? targetOrder.price
                            : (targetOrder.brokerOrderType === Broker_OrderType.STOPLOSS_MARKET_ORDER || targetOrder.brokerOrderType === Broker_OrderType.STOPLOSS_ORDER)
                                ? targetOrder.triggerPrice
                                : 0) / priceMultiplier
                    const quantity = targetOrder.quantity
                    let tlPriceFinal = price

                    let y = this.priceToCoordinate(price) as number
                    if (y) {
                        y = scaledCoordinate(y, vRatio)
                        if (this.drawingMode === 'DRAG' && this.draggedKey === tKey && this.dragType === 'ELEMENT') {
                            y = this.draggedPriceCoordinate!
                            tlPriceFinal = this.coordinateToPrice(this.draggedPriceCoordinate / this.verticalPixelRatio as Coordinate)
                        }
                        TPy = y
                    }
                    const profit = order.transactionType === TransactionType.BUY
                        ? (tlPriceFinal - openOrderprice) * quantity
                        : (openOrderprice - tlPriceFinal) * quantity

                    blocks.push({
                        key: tKey,
                        isHovered: this.hoveredKey ? hovered : true,
                        isVisible: this.hoveredKey ? visible : true,
                        draw: () => {
                            this.drawTradeBox(
                                ctx,
                                canvasWidth -
                                scaledCoordinate(50, this.horizontalPixelRatio),
                                y,
                                quantity,
                                profit,
                                'INR',
                                primary,
                                backgroundPaper,
                                textPrimary,
                                tKey,
                                targetOrder.transactionType,
                                undefined,
                                undefined,
                                profit>0,
                                false,
                                'targetOrder'
                            )
                        }
                    })
                    validOrderKey = !validOrderKey ? false : profit>0

                }
                if ([key, slKey, tKey].includes(this.hoveredKey)) {
                    blocks.push({
                        key: 'connector-lines',
                        isHovered: true,
                        isVisible: true,
                        draw: () => {
                            this.drawBracketOrderConnectingLines(ctx, y, SLy, TPy, primaryColor);
                        }
                    })
                }
                if(setEditOrderValidKey) {
                    this.editorderValid = validOrderKey
                }
            }
        }

        blocks.sort((a, b) => priority(a) - priority(b));

        // Draw in sorted order
        for (const block of blocks) {
            if(!this._chart?.touchEnabled) {
                if (block.isVisible) ctx.globalAlpha = 1;
                else ctx.globalAlpha = 0.5;
            } else {
                ctx.globalAlpha = 1;
            }
            block.draw();
        }
    }

    drawBracketOrderConnectingLines(ctx: CanvasRenderingContext2D, baseY: number, SLy: number | null, TPy: number | null, color: string) {
        const radius = scaledCoordinate(4, this.verticalPixelRatio)
        const rightBufferCoordinate = this.canvasWidth - scaledCoordinate(35, this.horizontalPixelRatio)
        ctx.globalAlpha = 1
        ctx.setLineDash([])
        if (SLy === null && TPy === null) {
            this.drawCircle(ctx, rightBufferCoordinate, baseY, radius, color, '#ffffff')
            return
        }
        if (SLy != null) {
            ctx.beginPath()
            ctx.strokeStyle = color
            ctx.lineWidth = scaledCoordinate(1, this.verticalPixelRatio)
            ctx.moveTo(rightBufferCoordinate, baseY)
            ctx.lineTo(rightBufferCoordinate, SLy)
            ctx.stroke()
        }
        if (TPy != null) {
            ctx.beginPath()
            ctx.strokeStyle = color
            ctx.lineWidth = scaledCoordinate(1, this.verticalPixelRatio)
            ctx.moveTo(rightBufferCoordinate, baseY)
            ctx.lineTo(rightBufferCoordinate, TPy)
            ctx.stroke()
        }
        this.drawCircle(ctx, rightBufferCoordinate, baseY, radius, color, '#ffffff')
        if (SLy != null) {
            this.drawCircle(ctx, rightBufferCoordinate, SLy, radius, color, '#ffffff')
        }
        if (TPy != null) {
            this.drawCircle(ctx, rightBufferCoordinate, TPy, radius, color, '#ffffff')
        }
    }

    drawCircle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, strokeColor: string, fillColor: string) {
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fillStyle = fillColor;
        ctx.fill();
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = scaledCoordinate(1, this.verticalPixelRatio);
        ctx.stroke();
        ctx.closePath();
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const hoveredData = this.findHoveredData(x, y)
        if (hoveredData) {
            this._hovered = true

            if (hoveredData.actionType === PositionComponentTypes.MODIFY_ORDER_LINE) {
                return {
                    externalId: this.id,
                    zOrder: 'top',
                    cursorStyle: 'ns-resize'
                }
            }

            return {
                externalId: this.id,
                zOrder: 'top',
                cursorStyle: 'pointer'
            }
        }

        if (this._hovered) {
            this._hovered = false
            this.hoveredKey = null
            this.hoveredComponent = undefined
            this._chart.callbacks.onTooltipDataChange?.(false, '', 0, 0)
        }

        return null
    }

    updateHandle = (id: string, time: Time, price: number) => {
        // intentionally empty – kept for API compatibility
    }

    onSettingsUpdate = (settings: Partial<OrdersOnChartToolDrawingSettings>) => {
        if (settings.positions) {
            this._state.positions = settings.positions
        }
        if (settings.openOrders) {
            this._state.openOrders = settings.openOrders
        }
        if (settings.ltp !== undefined) {
            this._state.ltp = settings.ltp
        }
        if (settings.topAsk !== undefined) {
            this._state.topAsk = settings.topAsk
        }
        if (settings.topBid !== undefined) {
            this._state.topBid = settings.topBid
        }

        this._settings = {
            ...this._settings,
            ...settings
        }

        this.primitive?.requestUpdate()
    }

    getAvailableSettings = (): DrawingSettingGroup<
        OrdersOnChartToolDrawingSettings,
        SettingValues
    >[] => {
        return []
    }

    getDrawingType(): DrawingTypes {
        switch (this.options.drawingType) {
            case 'chartOrdersActions':
                return 'chartOrdersActions'
        }
    }

    getSettingsValues(): OrdersOnChartToolDrawingSettings {
        return { ...this._settings }
    }

    getMiniSettings = () => {
        return []
    }

    drawHorizontalLine(ctx: CanvasRenderingContext2D, y: number, color: string, isDashed = false) {
        ctx.beginPath()
        ctx.strokeStyle = color
        ctx.lineWidth = scaledCoordinate(1, window.devicePixelRatio)
        if (isDashed) {
            // Example dash pattern — adjust as you like
            ctx.setLineDash([4, 2]);  // 4px dash, 2px gap
        } else {
            ctx.setLineDash([]);      // solid line
        }
        ctx.moveTo(0, y)
        ctx.lineTo(ctx.canvas.width, y)
        ctx.stroke()
        ctx.closePath()
    }

    drawHorzontalBox(ctx: CanvasRenderingContext2D, y: number, color: string, height: number) {
        ctx.beginPath()
        ctx.fillStyle = color
        ctx.rect(0, y, this.canvasWidth, height)
        ctx.fill
        ctx.closePath()
    }

    private ensureComponents(key: string): PositionComponents {
        let comps = this.positionsComponents[key]
        if (!comps) {
            comps = {}
            this.positionsComponents[key] = comps
        }
        return comps
    }

    drawAlertCircle(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        radius: number,
        color: string = 'red',
        textColor: string = '#ffffff',
        fontFamily: string = 'Arial'
    ) {
        if (!ctx) return;

        ctx.save();

        // Circle
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();

        // Text
        const fontSize = radius * 1.4;
        ctx.font = `bold ${fontSize}px ${fontFamily}`;
        ctx.fillStyle = textColor;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        ctx.fillText('!', x, y + radius * 0.05);

        ctx.restore();
    }

    drawTradeBox(
        ctx: CanvasRenderingContext2D,
        xRight: number,
        y: number,
        quantity: number,
        price: number,
        currency: string,
        primaryColor: string,
        background: string,
        textColor: string,
        key: string,
        type: TransactionType,
        product: Product,
        orderType: Broker_OrderType | undefined,
        validBox: boolean = true,
        isBasket: boolean = false,
        boxType: 'position' | 'openOrder' | 'stopLossOrder' | 'targetOrder',
        drawSlBox: boolean = false,
        drawTpBox: boolean = false,
        activeOrder = true,
    ) {
        if (y == null) return

        const vRatio = this.verticalPixelRatio
        const hRatio = this.horizontalPixelRatio
        const deviceRatio = window.devicePixelRatio || 1

        const adjustedFontSize = this.baseSize * deviceRatio
        const height = scaledCoordinate(18, vRatio)
        const padding = scaledCoordinate(8, vRatio)
        let spacing = scaledCoordinate(6, vRatio)
        const font = `${adjustedFontSize}px Arial`
        const lineColor = primaryColor
        const priceColor = price < 0 ? '#ff0000' : '#00aa00'
        const startY = y - height / 2
        const borderRadius = scaledCoordinate(4, deviceRatio)

        this.priceRange[key] = {
            y1: startY,
            y2: startY + height,
            y
        }

        ctx.font = font
        ctx.textBaseline = 'middle'

        if(!validBox) {
            ctx.save()

            ctx.beginPath()
            ctx.globalAlpha = 0.1
            ctx.fillStyle = 'red'
            ctx.fillRect(
                0,
                startY - 2 * this.verticalPixelRatio,
                this.canvasWidth,
                height + 4 * this.verticalPixelRatio
            )

            ctx.restore()
        }

        // Step 1: Horizontal Line
        this.drawHorizontalLine(ctx, y, lineColor, !activeOrder)

        // Background highlight for drag preview
        if (this.hoveredKey === key) {
            if (this.hoveredComponent === PositionComponentTypes.TARGET_PRICE_BTN) {
                const dragPrice =
                    this.drawingMode === 'DRAG'
                        ? this.draggedPriceCoordinate!
                        : type === TransactionType.BUY ? 0 : this.canvasHeight
                ctx.fillStyle = alpha(green, 0.1)
                const dragPriceOrY = this.drawingMode === 'DRAG'
                        ? (this.draggedPriceCoordinate - y)! : -y 
                if(isBasket){
                    ctx.fillRect(0, y, this.canvasWidth, dragPriceOrY)
                } else{
                    if (type === TransactionType.BUY) {
                        if (y - dragPrice > 0) {
                            ctx.fillRect(0, dragPrice, this.canvasWidth, y - dragPrice)
                        }
                    } else {
                        if (dragPrice - y > 0) {
                            ctx.fillRect(0, y, this.canvasWidth, dragPrice - y)
                        }
                    }
                }
            } else if (this.hoveredComponent === PositionComponentTypes.STOPLOSS_BTN) {
                const dragPrice =
                    this.drawingMode === 'DRAG'
                        ? this.draggedPriceCoordinate!
                        : type === TransactionType.BUY ? this.canvasHeight : 0
                const dragPriceOrY = this.drawingMode === 'DRAG'
                ? (this.draggedPriceCoordinate - y)! : this.canvasHeight -  y 
                ctx.fillStyle = alpha(yellow, 0.1)
                if(isBasket){
                    ctx.fillRect(0, y, this.canvasWidth, dragPriceOrY)
                } else{
                    if (type === TransactionType.BUY) {
                        if (dragPrice - y > 0) {
                            ctx.fillRect(0, y, this.canvasWidth, dragPrice - y)
                        }
                    } else {
                        if (y - dragPrice > 0) {
                            ctx.fillRect(0, dragPrice, this.canvasWidth, y - dragPrice)
                        }
                    }
                }
            }
        }

        const components = this.ensureComponents(key)

        // --- Price + quantity + close box ---
        let priceText = boxType === 'openOrder' ? type === TransactionType.BUY ? 'BUY' : 'SELL' : `${price > 0 ? '+' : ''}${price.toFixed(2)} ${currency}`
        if (boxType === 'openOrder' && orderType) {
            priceText += orderType === Broker_OrderType.LIMIT_ORDER ? " LIMIT" : [Broker_OrderType.STOPLOSS_MARKET_ORDER, Broker_OrderType.STOPLOSS_ORDER].includes(orderType) ? " STOP" : ''
        }
        const priceWidth = this.measure(ctx, priceText)
        const quantityText = `${quantity}`
        const quantityWidth = this.measure(ctx, quantityText)
        const closeIconWidth = scaledCoordinate(8, hRatio)
        const totalWidth =
            quantityWidth + priceWidth + padding * 6 + closeIconWidth
        let x = xRight - totalWidth

        // Outer box
        this.drawRoundedRect(ctx, x, startY, totalWidth, height, borderRadius)

        ctx.lineWidth = scaledCoordinate(2, deviceRatio)
        ctx.strokeStyle = primaryColor
        ctx.setLineDash(!activeOrder ? [4, 2] : [])
        ctx.stroke()

        // Quantity background
        const qtyBoxWidth = quantityWidth + padding * 2
        this.drawRoundedRect(
            ctx,
            x,
            startY,
            qtyBoxWidth,
            height,
            borderRadius,
            { topLeft: true, topRight: false, bottomLeft: true, bottomRight: false }
        )
        ctx.fillStyle = boxType === 'position' ? primaryColor : background
        ctx.fill()
        ctx.fillStyle = boxType === 'position' ? background : primaryColor
        ctx.textAlign = 'center'
        ctx.fillText(quantityText, x + qtyBoxWidth / 2, startY + height / 2)

        if (boxType !== 'position') {
            // quantity price splitter line
            ctx.beginPath()
            ctx.strokeStyle = alpha(primaryColor, 0.2)
            ctx.lineWidth = scaledCoordinate(2, vRatio)
            ctx.setLineDash([])
            ctx.moveTo(x + qtyBoxWidth, startY)
            ctx.lineTo(x + qtyBoxWidth, startY + height)
            ctx.stroke()
        }

        components.QUANTITY_BTN = {
            x1: x,
            x2: x + qtyBoxWidth
        }

        // Price text
        const priceBoxWidth = priceWidth + padding * 2
        this.drawRoundedRect(
            ctx,
            x + qtyBoxWidth,
            startY,
            priceBoxWidth,
            height,
            borderRadius,
            { topLeft: false, topRight: false, bottomLeft: false, bottomRight: false }
        )
        ctx.fillStyle = background
        ctx.fill()
        ctx.fillStyle = boxType === 'position' ? priceColor : primaryColor
        ctx.textAlign = 'left'
        ctx.fillText(priceText, x + qtyBoxWidth + padding, startY + height / 2)

        components.PRICE_BTN = {
            x1: x + qtyBoxWidth,
            x2: x + qtyBoxWidth + priceBoxWidth
        }

        x = x + qtyBoxWidth + priceBoxWidth

        // Close icon splitter line
        ctx.beginPath()
        ctx.strokeStyle = alpha(primaryColor, 0.2)
        ctx.lineWidth = scaledCoordinate(2, vRatio)
        ctx.setLineDash([])
        ctx.moveTo(x, startY)
        ctx.lineTo(x, startY + height)
        ctx.stroke()

        const closeIconBoxWidth = closeIconWidth + padding * 2
        this.drawRoundedRect(
            ctx,
            x,
            startY,
            closeIconBoxWidth,
            height,
            borderRadius,
            { topLeft: false, topRight: true, bottomLeft: false, bottomRight: true }
        )
        ctx.fillStyle = background
        ctx.fill()
        if (
            this.hoveredKey === key &&
            this.hoveredComponent === PositionComponentTypes.CANCEL_BTN
        ) {
            ctx.fillStyle = alpha(primaryColor, 0.1)
            ctx.fill()
        }
        ctx.fillStyle = primaryColor
        ctx.textAlign = 'left'
        ctx.fillText('X', x + padding, startY + height / 2)

        components.CANCEL_BTN = {
            x1: x,
            x2: x + closeIconBoxWidth
        }

        // Reset x for action buttons
        x = xRight - totalWidth

        // Helper for action button box
        const drawBox = (
            text: string,
            bgColor: string,
            fgColor: string,
            borderColor: string,
            componentType: PositionComponentTypes | null,
            isDashed = false
        ) => {
            if (!componentType) return

            let yRender = startY
            let isDragging = false

            if (
                this.hoveredKey === key &&
                componentType === PositionComponentTypes.TARGET_PRICE_BTN &&
                this.drawingMode === 'DRAG' &&
                this.dragType === 'TARGET'
            ) {
                yRender = this.draggedPriceCoordinate! - height / 2
                isDragging = true
            } else if (
                this.hoveredKey === key &&
                componentType === PositionComponentTypes.STOPLOSS_BTN &&
                this.drawingMode === 'DRAG' &&
                this.dragType === 'STOPLOSS'
            ) {
                yRender = this.draggedPriceCoordinate! - height / 2
                isDragging = true
            }

            const textWidth = this.measure(ctx, text)
            const boxWidth = textWidth + padding * 2
            x -= boxWidth + spacing

            if (isDragging) {
                const basePrice = this.coordinateToPrice((startY / this.verticalPixelRatio) as Coordinate)
                const latestPrice = this.coordinateToPrice((yRender / this.verticalPixelRatio) as Coordinate)
                const priceDiffNum = (latestPrice - basePrice)*quantity
                const priceDiff = (priceDiffNum).toFixed(2) + ' INR'
                let valid = true
                if(boxType==='position') {
                    if(this.dragType==='STOPLOSS') {
                        if(isBasket) {
                            valid = latestPrice < this._state.ltp
                        } else {
                            valid = type === TransactionType.BUY ? latestPrice < (this._state.topBid || this._state.ltp) : latestPrice > (this._state.topAsk || this._state.ltp)
                        }
                    } else if(this.dragType==='TARGET') {
                        if(isBasket) {
                            valid = latestPrice > this._state.ltp
                        } else {
                            valid = type === TransactionType.BUY ? latestPrice > (this._state.topBid || this._state.ltp) : latestPrice < (this._state.topAsk || this._state.ltp)
                        }
                    }
                } else if(boxType==='openOrder') {
                    if(this.dragType==='STOPLOSS') {
                        valid = type === TransactionType.BUY ? priceDiffNum < 0 : priceDiffNum > 0
                    } else if(this.dragType==='TARGET') {
                        valid = type === TransactionType.BUY ? priceDiffNum > 0 : priceDiffNum < 0
                    }
                }
                if(!valid) {
                    ctx.save()

                    ctx.beginPath()
                    ctx.globalAlpha = 0.1
                    ctx.fillStyle = 'red'
                    ctx.fillRect(
                        0,
                        yRender - 2 * this.verticalPixelRatio,
                        this.canvasWidth,
                        height + 4 * this.verticalPixelRatio
                    )

                    ctx.restore()
                }
                this.drawHorizontalLine(ctx, this.draggedPriceCoordinate!, borderColor)
                const priceDiffWidth = this.measure(ctx, priceDiff)
                const tempBoxWidth = priceDiffWidth + padding * 2
                const tempX = x + boxWidth
                this.drawRoundedRect(ctx, tempX, yRender, tempBoxWidth, height, borderRadius, { topLeft: false, bottomLeft: false, topRight: true, bottomRight: true })
                ctx.lineWidth = scaledCoordinate(2, deviceRatio)
                ctx.strokeStyle = borderColor
                ctx.setLineDash(isDashed ? [4, 2] : [])
                ctx.stroke()

                ctx.fillStyle = bgColor
                ctx.fill()

                ctx.fillStyle = fgColor
                ctx.textBaseline = 'middle'
                ctx.textAlign = 'center'
                ctx.fillText(priceDiff, tempX + tempBoxWidth / 2, yRender + height / 2)
            }

            this.drawRoundedRect(ctx, x, yRender, boxWidth, height, borderRadius, isDragging ? { topLeft: true, bottomLeft: true, topRight: false, bottomRight: false } : undefined)

            ctx.lineWidth = scaledCoordinate(2, deviceRatio)
            ctx.strokeStyle = borderColor
            ctx.setLineDash(isDashed ? [4, 2] : [])
            ctx.stroke()

            ctx.fillStyle = bgColor
            ctx.fill()

            if (
                this.hoveredKey === key &&
                this.hoveredComponent === componentType
            ) {
                ctx.fillStyle = alpha(borderColor, 0.1)
                ctx.fill()
            }

            ctx.fillStyle = fgColor
            ctx.textBaseline = 'middle'
            ctx.textAlign = 'center'
            ctx.fillText(text, x + boxWidth / 2, yRender + height / 2)

            components[componentType] = {
                x1: x,
                x2: x + boxWidth
            }
        }

        if (boxType === 'position' || boxType === 'openOrder') {
            const prevSpacing = spacing
            let stopLossDrawn = false

            if ((['position', 'openOrder'].includes(boxType) && drawSlBox)) {
                stopLossDrawn = true
                drawBox(
                    'SL',
                    background,
                    yellow,
                    yellow,
                    PositionComponentTypes.STOPLOSS_BTN,
                    true
                )
            } else {
                components.STOPLOSS_BTN = undefined
            }

            if ((['position', 'openOrder'].includes(boxType) && drawTpBox)) {
                if (stopLossDrawn) spacing = scaledCoordinate(1, vRatio)
                drawBox(
                    'TP',
                    background,
                    green,
                    green,
                    PositionComponentTypes.TARGET_PRICE_BTN,
                    true
                )
                spacing = prevSpacing
            } else {
                components.TARGET_PRICE_BTN = undefined
            }

            if (this.editOrderKey === key) {
                // Confirm / Discard
                drawBox(
                    'Confirm',
                    this.editorderValid ? primaryColor : background,
                    this.editorderValid ? background : alpha(textColor, 0.3),
                    this.editorderValid ? primaryColor : alpha(textColor, 0.3),
                    PositionComponentTypes.CONFIRM_BTN
                )
                spacing = prevSpacing

                drawBox(
                    'Discard',
                    background,
                    textColor,
                    alpha(textColor, 0.3),
                    PositionComponentTypes.DISCARD_BTN
                )
                spacing = prevSpacing
            } else {
                components.CONFIRM_BTN = undefined
                components.DISCARD_BTN = undefined
            }

            // Reverse only for position
            if (boxType === 'position' && !isBasket) {
                drawBox(
                    'RV',
                    background,
                    primaryColor,
                    primaryColor,
                    PositionComponentTypes.REVERSE_BTN
                )
            }
        }

        if (!validBox) {
            const radius = height/2 - 1*this.verticalPixelRatio
            x = x - padding - radius*0.7
            this.drawAlertCircle(ctx, x, y, radius)
            components.ALERT = {
                x1: x - radius,
                x2: x + radius
            }
        } else {
            components.ALERT = undefined
        }

        if ((boxType === 'position' || boxType === 'openOrder') && product) {
            const text = productToJSON(product)
            if(!validBox) {
                x = x - padding
            }
            drawBox(
                text,
                background,
                primaryColor,
                primaryColor,
                PositionComponentTypes.PRODUCT
            )
        }
    }

    // Draw rounded rect path
    drawRoundedRect(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        width: number,
        height: number,
        radius: number,
        radiusOptions = {
            topLeft: true,
            topRight: true,
            bottomRight: true,
            bottomLeft: true
        }
    ) {
        const r = {
            tl: radiusOptions.topLeft ? radius : 0,
            tr: radiusOptions.topRight ? radius : 0,
            br: radiusOptions.bottomRight ? radius : 0,
            bl: radiusOptions.bottomLeft ? radius : 0
        }

        ctx.beginPath()
        ctx.moveTo(x + r.tl, y)
        ctx.lineTo(x + width - r.tr, y)
        if (r.tr) ctx.quadraticCurveTo(x + width, y, x + width, y + r.tr)
        else ctx.lineTo(x + width, y)

        ctx.lineTo(x + width, y + height - r.br)
        if (r.br) ctx.quadraticCurveTo(x + width, y + height, x + width - r.br, y + height)
        else ctx.lineTo(x + width, y + height)

        ctx.lineTo(x + r.bl, y + height)
        if (r.bl) ctx.quadraticCurveTo(x, y + height, x, y + height - r.bl)
        else ctx.lineTo(x, y + height)

        ctx.lineTo(x, y + r.tl)
        if (r.tl) ctx.quadraticCurveTo(x, y, x + r.tl, y)
        else ctx.lineTo(x, y)

        ctx.closePath()
    }

    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {
        const { positions, openOrders: originalOpenOrders } = this._state

        const openOrders = [...originalOpenOrders]

        const draggedPrice = this.coordinateToPrice(this.draggedPriceCoordinate / this.verticalPixelRatio as Coordinate)
        const dragging = this.drawingMode === 'DRAG'

        if (this.editOrderKey && this.editOrderKey.includes('open-order-')) {
            const [index] = this.editOrderKey.split('-').splice(-1)
            if (Number(index) >= openOrders.length) openOrders.push(this.editOrders)
        }

        const data: LabelAxisViewConfig<number>[] = []

        positions.forEach((p, i)=>{
            const key = `position-${i}`
            const slKey = `position-stop-loss-order-${i}`
            const tKey = `position-target-order-${i}`
            const { position, stopLossOrder: slOrderOriginal, targetOrder: targetOrderOriginal } = p
            const stopLossOrder = (this.editOrderKey === key && this.editOrders?.SL) ? this.editOrders.SL : slOrderOriginal
            const targetOrder = (this.editOrderKey === key && this.editOrders?.TP) ? this.editOrders.TP : targetOrderOriginal
            if (position) {
                const p = position
                const transactionType: TransactionType =
                    p.closePosition < 0
                        ? TransactionType.SELL
                        : p.closePosition > 0
                            ? TransactionType.BUY
                            : undefined
    
                if (transactionType) {
                    const primaryColor =
                        transactionType === TransactionType.BUY ? blue : red
                    const price =
                        (p.closePosition !== 0
                            ? p.closePositionValue / Math.abs(p.closePosition)
                            : 0) / priceMultiplier
    
                    data.push({
                        id: key,
                        p1: position.isBasket ? price : Math.abs(price),
                        p2: position.isBasket ? price : Math.abs(price),
                        fill: true,
                        fillColor: primaryColor,
                        labelColors: {
                            p1: primaryColor,
                            p2: primaryColor
                        }
                    })
                    if (stopLossOrder) {
                        const slPrice = (dragging && this.draggedKey === slKey) ? draggedPrice :
                            (stopLossOrder.brokerOrderType === Broker_OrderType.LIMIT_ORDER
                                ? stopLossOrder.price
                                : (stopLossOrder.brokerOrderType === Broker_OrderType.STOPLOSS_MARKET_ORDER || stopLossOrder.brokerOrderType === Broker_OrderType.STOPLOSS_ORDER)
                                    ? stopLossOrder.triggerPrice
                                    : 0) / priceMultiplier
                        data.push({
                            id: slKey,
                            p1: position.isBasket ? slPrice : Math.abs(slPrice),
                            p2: position.isBasket ? slPrice : Math.abs(slPrice),
                            fill: true,
                            fillColor: yellow,
                            labelColors: {
                                p1: yellow,
                                p2: yellow
                            }
                        })
                    }
        
                    if (targetOrder) {
                        const tpPrice = (dragging && this.draggedKey === tKey) ? draggedPrice :
                            (targetOrder.brokerOrderType === Broker_OrderType.LIMIT_ORDER
                                ? targetOrder.price
                                : (targetOrder.brokerOrderType === Broker_OrderType.STOPLOSS_MARKET_ORDER || targetOrder.brokerOrderType === Broker_OrderType.STOPLOSS_ORDER)
                                    ? targetOrder.triggerPrice
                                    : 0) / priceMultiplier
        
                        data.push({
                            id: tKey,
                            p1: position.isBasket ? tpPrice : Math.abs(tpPrice),
                            p2:  position.isBasket ? tpPrice : Math.abs(tpPrice),
                            fill: true,
                            fillColor: green,
                            labelColors: {
                                p1: green,
                                p2: green
                            }
                        })
                    }
                }
            }
        })

        if (openOrders && openOrders.length) {
            for (let i = 0; i < openOrders.length; i++) {
                let o = openOrders[i]?.order
                let sl = openOrders[i]?.SL
                let tp = openOrders[i]?.TP
                if (this.editOrderKey === `open-order-${i}`) {
                    if (this.editOrders?.order) o = this.editOrders.order
                    if (this.editOrders?.SL) sl = this.editOrders.SL
                    if (this.editOrders?.TP) tp = this.editOrders.TP
                }

                if (o) {
                    const primaryColor = o.transactionType === TransactionType.BUY ? blue : red
                    const price = (dragging && this.draggedKey === `open-order-${i}`) ? draggedPrice : (o.brokerOrderType === Broker_OrderType.LIMIT_ORDER
                        ? o.price
                        : (o.brokerOrderType === Broker_OrderType.STOPLOSS_MARKET_ORDER || o.brokerOrderType === Broker_OrderType.STOPLOSS_ORDER)
                            ? o.triggerPrice
                            : 0) / priceMultiplier

                    data.push({
                        id: `price-order-${i}`,
                        p1: price,
                        p2: price,
                        fill: true,
                        fillColor: primaryColor,
                        labelColors: {
                            p1: primaryColor,
                            p2: primaryColor
                        }
                    })
                }


                if (sl) {
                    const slPrice = (dragging && this.draggedKey === `stop-loss-order-${i}`) ? draggedPrice :
                        (sl.brokerOrderType === Broker_OrderType.LIMIT_ORDER
                            ? sl.price
                            : (sl.brokerOrderType === Broker_OrderType.STOPLOSS_MARKET_ORDER || sl.brokerOrderType === Broker_OrderType.STOPLOSS_ORDER)
                                ? sl.triggerPrice
                                : 0) / priceMultiplier
                    data.push({
                        id: `price-stoploss-order-${i}`,
                        p1: slPrice,
                        p2: slPrice,
                        fill: true,
                        fillColor: yellow,
                        labelColors: {
                            p1: yellow,
                            p2: yellow
                        }
                    })
                }
                if (tp) {
                    const tpPrice = (dragging && this.draggedKey === `target-order-${i}`) ? draggedPrice :
                        (tp.brokerOrderType === Broker_OrderType.LIMIT_ORDER
                            ? tp.price
                            : (tp.brokerOrderType === Broker_OrderType.STOPLOSS_MARKET_ORDER || tp.brokerOrderType === Broker_OrderType.STOPLOSS_ORDER)
                                ? tp.triggerPrice
                                : 0) / priceMultiplier

                    data.push({
                        id: `price-target-order-${i}`,
                        p1: tpPrice,
                        p2: tpPrice,
                        fill: true,
                        fillColor: green,
                        labelColors: {
                            p1: green,
                            p2: green
                        }
                    })
                }
            }
        }

        return data
    }

    private captureHandleScrollSnapshot(): HandleScrollSnapshot {
        const hs = this._chartApi?.options()?.handleScroll
        if (hs && typeof hs === 'object') {
            const o = hs as unknown as Record<string, boolean | undefined>
            return {
                mouseWheel: Boolean(o.mouseWheel),
                pressedMouseMove: Boolean(o.pressedMouseMove),
                pressedHorizontalMouseMove: Boolean(o.pressedHorizontalMouseMove),
                pressedVerticalMouseMove: Boolean(o.pressedVerticalMouseMove),
                horzTouchDrag: Boolean(o.horzTouchDrag),
                vertTouchDrag: Boolean(o.vertTouchDrag)
            }
        }
        return defaultHandleScrollSnapshot()
    }

    onMouseDownClick = (event: MouseEvent | TouchEvent) => {
        const touch = (event as TouchEvent)?.touches?.[0]
        this._chartBounds = this.getPaneElement().getBoundingClientRect()
        this.onMouseDownTime = new Date()

        const chartX = determineChartX(this._chartApi, this._chartBounds, this._chart.touchEnabled ? (touch?.clientX ?? 0) : ((event as MouseEvent)?.clientX ?? 0))
        const chartY = determineChartY(this._chartApi, this._chartBounds, this._chart.touchEnabled ? (touch?.clientY ?? 0) : ((event as MouseEvent)?.clientY ?? 0))

        const element = this.findHoveredData(chartX, chartY)
        if (element) {
            if (!this.handleScrollSnapshotBeforeOrderInteraction) {
                this.handleScrollSnapshotBeforeOrderInteraction = this.captureHandleScrollSnapshot()
            }
            const snap = this.handleScrollSnapshotBeforeOrderInteraction
            this._chart.updateLayoutOptions({
                handleScroll: {
                    ...snap,
                    pressedMouseMove: false,
                    pressedHorizontalMouseMove: false,
                    pressedVerticalMouseMove: false
                }
            })
        }

        if (!element || this.drawingMode !== 'NORMAL') return

        if (element.actionType === PositionComponentTypes.TARGET_PRICE_BTN) {
            this.draggedKey = element.key
            this.dragStartY = chartY
            this.dragType = 'TARGET'
            this.draggedPriceCoordinate = scaledCoordinate(chartY, this.verticalPixelRatio)
            return
        }

        if (element.actionType === PositionComponentTypes.STOPLOSS_BTN) {
            this.draggedKey = element.key
            this.dragStartY = chartY
            this.dragType = 'STOPLOSS'
            this.draggedPriceCoordinate = scaledCoordinate(chartY, this.verticalPixelRatio)
            return
        }

        // if (element.actionType === PositionComponentTypes.MODIFY_ORDER_LINE || ['target-order', 'stop-loss-order'].includes(element.key)) {
        if (element && !positionRegex.test(element.key)) {
            this.draggedKey = element.key
            this.dragStartY = chartY
            this.dragType = 'ELEMENT'
            this.draggedPriceCoordinate = scaledCoordinate(chartY, this.verticalPixelRatio)
            return
        }
    }

    setEditOrderKey = (key: string | undefined) => {
        if (this.editOrderKey !== key) {
            this.editOrderKey = key
            this.editOrders = undefined
        }
        this.updateDrawing()
    }

    onMouseUpClick = (event: MouseEvent | TouchEvent) => {
        this._chartBounds = this.getPaneElement().getBoundingClientRect()
        this.onMouseDownTime = new Date()

        if (this.drawingMode === 'DRAG') {
            if (this.draggedKey && this.draggedPriceCoordinate != null) {
                const chartPrice = this.coordinateToPrice(this.draggedPriceCoordinate / this.verticalPixelRatio as Coordinate)
                const price = Number(this.roundPriceToTickSize(chartPrice)) * priceMultiplier
                let position: Position & { isBasket? : boolean } | undefined
                let draggedOrder: Order | undefined
                const [index] = this.draggedKey.split('-').splice(-1)

                if(positionRegex.test(this.draggedKey)) {
                    position = this._state.positions[Number(index)].position
                } else if (positionStopLossRegex.test(this.draggedKey)) {
                    if(`position-${index}` === this.editOrderKey && this.editOrders.SL) {
                        draggedOrder = this.editOrders.SL
                    } else {
                        draggedOrder = this._state.positions[Number(index)].stopLossOrder ?? undefined
                    }
                } else if(positionTargetRegex.test(this.draggedKey)) {
                    if(`position-${index}` === this.editOrderKey && this.editOrders.TP) {
                        draggedOrder = this.editOrders.TP
                    } else {
                        draggedOrder = this._state.positions[Number(index)].targetOrder ?? undefined
                    }
                } else if(openOrderRegex.test(this.draggedKey)) {
                    if(this.draggedKey === this.editOrderKey && this.editOrders.order) {
                        draggedOrder = this.editOrders.order
                    } else {
                        draggedOrder = this._state.openOrders[Number(index)].order
                    }
                } else if(openOrderStopLossRegex.test(this.draggedKey)) {
                    if(`open-order-${index}` === this.editOrderKey && this.editOrders.SL) {
                        draggedOrder = this.editOrders.SL
                    } else {
                        draggedOrder = this._state.openOrders[Number(index)].SL ?? undefined
                    }
                } else if(openOrderTargetRegex.test(this.draggedKey)) {
                    if(`open-order-${index}` === this.editOrderKey && this.editOrders.TP) {
                        draggedOrder = this.editOrders.TP
                    } else {
                        draggedOrder = this._state.openOrders[Number(index)].TP ?? undefined
                    }
                }
                    const transactionType: TransactionType = position ?
                        position.closePosition < 0
                            ? TransactionType.SELL
                            : position.closePosition > 0
                                ? TransactionType.BUY
                                : undefined :
                        draggedOrder ? draggedOrder.transactionType : undefined
                    const security = position ? position.security : draggedOrder.security
                    const quantity = position ? Math.abs(position.closePosition) : draggedOrder.quantity
                    if(transactionType) {
                        if (this.dragType === 'ELEMENT') {
                            let keyString = 'order'
                            if (this.draggedKey.includes('position-stop-loss-order') || this.draggedKey.includes('position-target-order')) {
                                // const [index] = clickData.key.split('-').splice(-1)
                                if (this.draggedKey === `position-stop-loss-order-${index}`) keyString = 'SL'
                                else keyString = 'TP'
                                this.setEditOrderKey(`position-${index}`)
                            } else {
                                // const [index] = clickData.key.split('-').splice(-1)
                                if (this.draggedKey.includes('open-order')) {
                                    keyString = 'order'
                                } else if (this.draggedKey.includes('stop-loss')) {
                                    keyString = 'SL'
                                } else if (this.draggedKey.includes('target')) {
                                    keyString = 'TP'
                                }
                                const orderKey = `open-order-${index}`
                                this.setEditOrderKey(orderKey)
                            }
                            const priceKey = draggedOrder.brokerOrderType === Broker_OrderType.LIMIT_ORDER ?
                                'price' : [Broker_OrderType.STOPLOSS_ORDER, Broker_OrderType.STOPLOSS_MARKET_ORDER].includes(draggedOrder.brokerOrderType) ? 'triggerPrice' : 'price'
                            const chartPrice = this.coordinateToPrice(this.draggedPriceCoordinate / this.verticalPixelRatio as Coordinate)
                            const price = Number(this.roundPriceToTickSize(chartPrice)) * priceMultiplier
                            const order = draggedOrder
                            const orderData: Order = {
                                security: order.security,
                                transactionType: order.transactionType,
                                orderType: order.orderType,
                                brokerOrderType: order.brokerOrderType,
                                quantity: order.quantity,
                                isBasket: order.isBasket,
                                orderDate: Date.now(),
                                product: order.product,
                                price: Math.round(order.price),
                                triggerPrice: Math.round(order.triggerPrice),
                                brokerVariety: order.brokerVariety,
                                validity: order.validity,
                                clientOrderId: order.clientOrderId,
                                disclosedQuantity: order.disclosedQuantity,
                                orderId: order.orderId,
                                basketId: order.basketId,
                                immediateExecutePaperOrder: false
                            }
                            this.editOrders = {
                                ...(this.editOrders ?? {} as any),
                                [keyString]: {
                                    ...orderData,
                                    [priceKey]: price
                                }
                            }
                        } else if (this.dragType === 'STOPLOSS') {
                                this.setEditOrderKey(this.draggedKey)
                                let product = Product.INTRADAY
                                if(positionRegex.test(this.draggedKey)) {
                                    product = position?.product??Product.INTRADAY
                                } else if (openOrderRegex.test(this.draggedKey)) {
                                    product = draggedOrder?.product??Product.INTRADAY
                                }
                                const isBasket = position?.isBasket ?? false
                                this.editOrders = {
                                    ...(this?.editOrders),
                                    SL: {
                                        security,
                                        transactionType: isBasket ? TransactionType.BUY : (transactionType === TransactionType.BUY ? TransactionType.SELL : TransactionType.BUY),
                                        orderType: OrderType.STOP_LOSS,
                                        brokerOrderType: Broker_OrderType.STOPLOSS_MARKET_ORDER,
                                        quantity: isBasket ? 1 : quantity,
                                        isBasket: isBasket,
                                        // batchId,
                                        orderDate: Date.now(),
                                        product,
                                        price: 0,
                                        triggerPrice: Math.round(price),
                                        brokerVariety: Broker_Variety.REGULAR,
                                        validity: Validity.DAY,
                                        clientOrderId: uuid(),
                                        marketProtection: -1,
                                        immediateExecutePaperOrder: false
                                    }
                                }
                            } else if (this.dragType === 'TARGET') {
                                this.setEditOrderKey(this.draggedKey)
                                let product = Product.INTRADAY
                                if(positionRegex.test(this.draggedKey)) {
                                    product = position?.product??Product.INTRADAY
                                } else if (openOrderRegex.test(this.draggedKey)) {
                                    product = draggedOrder?.product??Product.INTRADAY
                                }
                                const isBasket = position?.isBasket ?? false
                                this.editOrders = {
                                    ...(this?.editOrders),
                                    TP: {
                                        security,
                                        transactionType: isBasket ? TransactionType.BUY : (transactionType === TransactionType.BUY ? TransactionType.SELL : TransactionType.BUY),
                                        orderType: OrderType.TARGET,
                                        brokerOrderType: Broker_OrderType.LIMIT_ORDER,
                                        quantity: isBasket ? 1 : quantity,
                                        isBasket: isBasket,
                                        // batchId,
                                        orderDate: Date.now(),
                                        product,
                                        price: Math.round(price),
                                        triggerPrice: 0,
                                        brokerVariety: Broker_Variety.REGULAR,
                                        validity: Validity.DAY,
                                        clientOrderId: uuid(),
                                        immediateExecutePaperOrder: false
                                    }
                                }
                            }
                    }
                }
        }

        this.drawingMode = 'NORMAL'
        this.dragStartY = undefined
        this.draggedKey = null
        this.dragType = undefined
        this.hoveredKey = null
        this.hoveredComponent = undefined
        this.updateDrawing()

        if (this.handleScrollSnapshotBeforeOrderInteraction) {
            this._chart.updateLayoutOptions({
                handleScroll: { ...this.handleScrollSnapshotBeforeOrderInteraction }
            })
            this.handleScrollSnapshotBeforeOrderInteraction = null
        }
    }

    destroy(publish?: boolean, pushToHistory?: boolean, chartTypeToHistory?: boolean): void {
        this.unSubscribeMouseUpDownMove()
        this._chartApi.unsubscribeClick(this.handleChartClick)
        this.positionsComponents = {}
        this.priceRange = {}
        if (this.handleScrollSnapshotBeforeOrderInteraction) {
            this._chart.updateLayoutOptions({
                handleScroll: { ...this.handleScrollSnapshotBeforeOrderInteraction }
            })
            this.handleScrollSnapshotBeforeOrderInteraction = null
        }
        super.destroy()
    }
}

export default OrdersOnChartTool
